import { errorCodeOf, itemLinesOf } from '@/components/generationBasis'
import type {
    AdHistoryGroupStatus,
    AdHistoryMeasures,
    AdHistoryOther,
    AdHistoryWinner,
    AdHistoryWinners,
} from './types'

export const AD_HISTORY_UNAVAILABLE = 'AD_HISTORY_UNAVAILABLE'
export const FORBIDDEN = 'FORBIDDEN'

/** The most recent results may not be used once they are older than this many days. */
export const STALE_AFTER_DAYS = 14

/** What a copy needs before it can be compared (shown as "needs N more"); the same floors the ranking applies. */
export const MIN_RESULTS: Record<string, number> = { leads: 10, purchases: 5 }
export const MIN_DELIVERY_DAYS = 7
export const MIN_IMPRESSIONS = 1000

export const REFRESH_LIMIT_HOURS = 6

export const AD_HISTORY_UNAVAILABLE_MESSAGE = 'Past ad results could not be read right now. Please try again.'
export const AD_HISTORY_FORBIDDEN_MESSAGE = 'Past ad results are available to the owner of this campaign only.'
export const AD_HISTORY_OFF_MESSAGE = 'Learning from your own ad history is not switched on for this campaign.'
export const AD_HISTORY_ACTION_FAILED_MESSAGE = 'We could not save that change. Please try again.'
export const STALE_NOTE = `Results are older than ${STALE_AFTER_DAYS} days and are not used.`

export type AdHistoryErrorKind = 'forbidden' | 'unavailable' | 'other'

/** Which message a failed read or change needs: no access, a retryable failure, or anything else. */
export const adHistoryErrorKind = (error: unknown): AdHistoryErrorKind => {
    const code = errorCodeOf(error)
    if (code === FORBIDDEN) return 'forbidden'
    if (code === AD_HISTORY_UNAVAILABLE) return 'unavailable'
    return 'other'
}

/** A day written YYYY-MM-DD, without shifting it into another day by a time zone. */
export const formatDay = (day: string | null | undefined): string | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day ?? '')
    if (!match) return null
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/** A moment, in the viewer's time zone unless one is given. */
export const formatMoment = (value: string | null | undefined, timeZone?: string): string | null => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short', ...(timeZone ? { timeZone } : {}) })
}

const countOf = (count: number, noun: string) => `${count.toLocaleString('en-US')} ${noun}${count === 1 ? '' : 's'}`

export const formatMoney = (amount: number, currency: string | null): string => {
    if (currency) {
        try {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
        } catch {
            return `${amount.toFixed(2)} ${currency}`
        }
    }
    return amount.toFixed(2)
}

/** The probability as a percentage that never rounds up to 100. */
export const formatConfidence = (pBest: number | null | undefined): string | null => {
    if (typeof pBest !== 'number' || Number.isNaN(pBest)) return null
    const percent = Math.min(99.9, Math.max(0, Math.floor(pBest * 1000) / 10))
    return `${percent.toFixed(1)}%`
}

const resultNoun = (resultKind: string | null) => (resultKind === 'purchases' ? 'purchase' : 'lead')

export type StatusTone = 'ok' | 'warning' | 'error'

export interface GroupStatusNote {
    text: string
    tone: StatusTone
    /** The owner needs to reconnect Meta */
    reconnect: boolean
}

const NO_PROGRESS_NOTE =
    'History is loading slowly: this ad account is shared by many licences or this licence has more ad results a day than can be read in one run; it continues with the next daily reads.'
const DATA_TOO_LARGE_NOTE =
    'One day of results for this ad account is too large to read automatically; support has been notified.'

/** The sentence for the state of one ad account's daily read. */
export const groupStatusNote = (group: AdHistoryGroupStatus, timeZone?: string): GroupStatusNote => {
    switch (group.status) {
        case 'ok': {
            const day = formatDay(group.lastSuccessUntil)
            return { text: day ? `Updated ${day}` : 'Waiting for the first read', tone: 'ok', reconnect: false }
        }
        case 'partial':
            return {
                text: `Loading history: ${countOf(group.daysStored, 'day')} so far, continues with the next daily read`,
                tone: 'warning',
                reconnect: false,
            }
        case 'rate_limited': {
            const next = formatMoment(group.nextAttemptAt, timeZone)
            return {
                text: `Meta is limiting requests for this ad account; ${next ? `next try ${next}` : 'it will try again later'}`,
                tone: 'warning',
                reconnect: false,
            }
        }
        case 'needs_reauth':
            return { text: 'Reconnect Meta', tone: 'error', reconnect: true }
        case 'missing_permission':
            return {
                text: 'The Meta connection cannot read ad results; reconnect and grant access',
                tone: 'error',
                reconnect: true,
            }
        case 'account_changed':
            return {
                text: 'Your campaigns use another ad account than your Meta connection',
                tone: 'error',
                reconnect: false,
            }
        case 'failed':
            return {
                text: 'Could not read results; retried with the next daily read',
                tone: 'warning',
                reconnect: false,
            }
        case 'stalled':
            return group.stallReason === 'data_too_large'
                ? { text: DATA_TOO_LARGE_NOTE, tone: 'error', reconnect: false }
                : { text: NO_PROGRESS_NOTE, tone: 'warning', reconnect: false }
        default:
            return { text: 'The state of this read is not known', tone: 'warning', reconnect: false }
    }
}

/** Plain sentence for the state of the winning copy as a source, or null when nothing needs saying. */
export const winnersStateNote = (winners: Pick<AdHistoryWinners, 'state' | 'reason' | 'mode'>): string | null => {
    const { state, reason } = winners
    if (state === 'off' || winners.mode === 'off') return 'Winning copy is not used for this kind of generation.'
    if (state === 'insufficient') {
        if (reason === 'no_history') return 'No results have been read yet.'
        if (reason === 'no_usable_winners') return 'Winning copy was found, but none of it can be used for generation.'
        return 'No ad copy leads yet.'
    }
    if (state === 'stale') {
        return `${STALE_NOTE} Your approvals are kept and apply again when the results are fresh.`
    }
    if (state === 'unavailable') {
        if (reason === 'special_ad_category') return 'Winning copy is not used for special ad categories.'
        if (reason === 'purpose_off') return 'Winning copy is not used for this kind of generation.'
        return 'Winning copy could not be read right now.'
    }
    return null
}

const DROP_REASON_TEXT: Record<string, string> = {
    too_long: 'it is longer than the limits for generation',
    sensitive_theme: 'it names a sensitive subject',
    demographic: 'it names who people are',
    duplicate: 'another copy that reads the same is used instead',
    over_budget: 'too many copies were offered at once',
    empty: 'nothing is left of it once it is cleaned',
}

export const dropReasonText = (reason: string | null): string => {
    const detail = reason ? DROP_REASON_TEXT[reason] : null
    return detail ? `Cannot be used for generation: ${detail}.` : 'Cannot be used for generation.'
}

/** Why a winner that generation is not offered now is not offered. */
export const notOfferedText = (winners: Pick<AdHistoryWinners, 'state' | 'reason'>): string => {
    if (winners.state === 'stale') return 'Not offered: the results are older than 14 days.'
    if (winners.state === 'unavailable' && winners.reason === 'special_ad_category')
        return 'Not offered: special ad categories do not use past ads.'
    if (winners.state === 'unavailable' && winners.reason === 'purpose_off')
        return 'Not offered: winning copy is not used for this kind of generation.'
    return 'Not offered for generation right now.'
}

export const winnerStateLabel = (winner: AdHistoryWinner, mode: string): string | null => {
    switch (winner.state) {
        case 'used':
            return mode === 'manual' ? 'Approved: used in generation' : 'Used in generation'
        case 'proposed':
            return mode === 'manual' ? 'Waiting for your approval: not used yet' : 'Not used right now'
        case 'excluded':
            return 'Excluded: never used'
        default:
            return null
    }
}

/** The lines to show for copy: the headline and the primary text, as the generation basis shows them. */
export const copyLinesOf = ({ headline, primaryText }: Pick<AdHistoryMeasures, 'headline' | 'primaryText'>) =>
    itemLinesOf({ kind: 'winning_ad', text: null, fields: { headline, primary_text: primaryText } })

/** "12 leads, $8.40 per lead, $100.00 spent over 20 days" */
export const resultsLine = (measures: AdHistoryMeasures): string => {
    const noun = resultNoun(measures.resultKind)
    const parts = [countOf(measures.results, noun)]
    if (measures.costPerResult !== null && measures.costPerResult !== undefined) {
        parts.push(`${formatMoney(measures.costPerResult, measures.currency)} per ${noun}`)
    }
    parts.push(`${formatMoney(measures.spend, measures.currency)} spent over ${countOf(measures.deliveryDays, 'day')}`)
    return parts.join(', ')
}

/** What a copy that does not lead yet is missing. */
export const otherReasonTexts = (other: AdHistoryOther): string[] =>
    other.reasons.map((reason) => {
        switch (reason) {
            case 'text_unknown':
                return 'The text that ran is not known, so it is not compared.'
            case 'single_qualifying_copy':
                return 'Needs another copy in the same ad set with enough results to compare with.'
            case 'not_enough_evidence':
                return 'Not enough evidence yet that it does better than the others.'
            case 'results_below_floor': {
                const floor = MIN_RESULTS[other.resultKind ?? 'leads'] ?? MIN_RESULTS.leads
                const missing = Math.max(1, Math.ceil(floor - other.results))
                return `Needs ${missing} more ${resultNoun(other.resultKind)}${missing === 1 ? '' : 's'}.`
            }
            case 'delivery_days_below_floor': {
                const missing = Math.max(1, MIN_DELIVERY_DAYS - other.deliveryDays)
                return `Needs ${countOf(missing, 'more day')} of delivery.`
            }
            case 'impressions_below_floor':
                return `Needs at least ${MIN_IMPRESSIONS.toLocaleString('en-US')} impressions.`
            case 'spend_below_floor':
                return 'Needs more spend before it can be compared.'
            default:
                return 'Not enough evidence yet.'
        }
    })
